import type { DraftPayment, Payment as PaymentType } from "@/apis/gql/types";
import { ErrorMessage } from "@/components/ui/error";
import { LoadingSpinner } from "@/components/ui/spinner";
import { useInitiatePayment } from "@/queries/payments/initiate-payment";
import { groupBy, isEmpty } from "lodash";
import { useQueryState } from "nuqs";
import { useMemo } from "react";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useBackfillDraftPaymentDetails } from "@/queries/invoices/set-payment-details";
import {
  type CreatePaymentInput,
  useCreatePayment,
} from "@/queries/payments/create-payment";
import { PaymentsComponent } from "./payments";

type Props = {
  invoiceIds: string[];
};

export const _Payments = ({ invoiceIds }: Props) => {
  const { t } = useTranslation();
  const [webformId, setWebformId] = useQueryState("webform_id");
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const [draftPayments, setDraftPayments] = useState<DraftPayment[]>([]);

  const [backfillDraftPayments] = useBackfillDraftPaymentDetails();
  const [createPayment] = useCreatePayment();

  const { initiatePayment } = useInitiatePayment({
    config: {
      showToasts: false,
    },
  });

  const handleFetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const payments = await backfillDraftPayments({ invoiceIds });
      setDraftPayments(payments ?? []);
    } catch (error) {
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }, [backfillDraftPayments, invoiceIds]);

  useEffect(() => {
    handleFetchInvoices();
  }, [...invoiceIds]);

  const onAccountSelection = useCallback(
    async (
      bankAccountId: string,
      bankConnectionId: string,
      payments: PaymentType[],
    ) => {
      const webform = await initiatePayment({
        payments,
        bankAccountId,
        bankConnectionId,
      });

      setWebformId(webform.id);
    },
    [initiatePayment, invoiceIds],
  );

  const handleConfirmPayments = useCallback(
    async (
      draftPayments: {
        draftPaymentIds: string[];
        input: Partial<CreatePaymentInput>;
      }[],
    ) => {
      try {
        const payments = await Promise.all(
          draftPayments.map(async (payment) => {
            const createdPayment = await createPayment(
              payment.draftPaymentIds,
              payment.input,
            );
            return createdPayment;
          }),
        );

        return payments;
      } catch (error) {
        toast.error(t("generic_error"));
        throw error;
      }
    },
    [createPayment, t],
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || isEmpty(draftPayments) || !draftPayments) {
    return <ErrorMessage retry={handleFetchInvoices} />;
  }

  return (
    <PaymentsComponent
      webformId={webformId}
      payments={draftPayments}
      invoiceIds={invoiceIds}
      onAccountSelection={onAccountSelection}
      onConfirmPayments={handleConfirmPayments}
    />
  );
};

export const Payments = memo(_Payments);
